import { useEffect, useState, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import * as Location from 'expo-location';
import * as TaskManager from 'expo-task-manager';
import * as Notifications from 'expo-notifications';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../lib/i18n';
import { refreshDrivingStatusCountdown } from '../lib/drivingLimitStatusNotification';
import { track } from '../lib/analytics';
import {
  normalizeLocationError,
  LOCATION_ERROR,
  isUserStateLocationError,
  isValidLocationCoordinates,
} from '../utils/locationUtils';
import sentry from '../utils/sentry';
import {
  CANCEL_NOTIFICATION_ACTION_ID,
  DRIVING_ALERT_NOTIFICATION_CATEGORY,
  MOVEMENT_ALERT_NOTIFICATION_CATEGORY,
  START_DRIVING_ACTION_ID,
  STOP_DRIVING_ACTION_ID,
  dismissDrivingAlertNotifications,
  getDrivingAlertNotificationId,
  isDrivingAlertNotificationResponse,
  isMovementAlertNotificationResponse,
} from '../lib/drivingNotifications';

const BACKGROUND_LOCATION_TASK = 'background-location-task';
const LOCATION_UPDATE_INTERVAL = 30000; // 30 seconds
const FOREGROUND_UPDATE_INTERVAL = 10000; // 10 seconds for foreground updates
const FOREGROUND_DISTANCE_INTERVAL = 10; // 10 meters minimum movement before callback
const STATIONARY_CHECK_INTERVAL = 30000; // 30 seconds for stationary checks
const FOREGROUND_PROCESSING_COOLDOWN_MS = 1000; // prevent multiple updates per second
const MOVEMENT_ALERT_COOLDOWN_MS = 10000; // avoid movement alert spam
const MAX_ACCEPTED_ACCURACY_METERS = 50; // ignore very noisy points

// Add a web check with correct typing
const isWeb = (Platform.OS as any) === 'web';
const isAndroid = Platform.OS === 'android';
const shouldEnableNativeBackgroundLocationTask = !isWeb;
const BACKGROUND_ALERT_STATE_STORAGE_KEY = 'backgroundAlertState';

type BackgroundAlertType = 'movement' | 'stationary' | null;

type BackgroundAlertDecision =
  | 'idle'
  | 'movement_edge'
  | 'movement_duplicate'
  | 'stationary_edge'
  | 'stationary_duplicate'
  | 'stationary_repeat'
  | 'reset';

interface BackgroundAlertState {
  version: 1;
  activeAlert: BackgroundAlertType;
  lastDecision: BackgroundAlertDecision;
  lastScheduledAt: number | null;
  updatedAt: number | null;
}

interface BackgroundLocationTaskPayload {
  locations: Location.LocationObject[];
}

const DEFAULT_BACKGROUND_ALERT_STATE: BackgroundAlertState = {
  version: 1,
  activeAlert: null,
  lastDecision: 'idle',
  lastScheduledAt: null,
  updatedAt: null,
};

const TRACKING_STORAGE_KEYS = [
  'stableLocation',
  'locationTimestamp',
  'lastLocation',
  BACKGROUND_ALERT_STATE_STORAGE_KEY,
] as const;

function getDistance(coord1: { latitude: number; longitude: number }, coord2: { latitude: number; longitude: number }) {
  const R = 6371e3;
  const lat1 = (coord1.latitude * Math.PI) / 180;
  const lat2 = (coord2.latitude * Math.PI) / 180;
  const deltaLat = ((coord2.latitude - coord1.latitude) * Math.PI) / 180;
  const deltaLon = ((coord2.longitude - coord1.longitude) * Math.PI) / 180;
  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const safeParseJSON = <T>(value: string | null): T | null => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch (err) {
    console.warn('Failed to parse JSON from storage value', { value, error: err });
    return null;
  }
};

const buildBackgroundAlertState = (
  overrides: Partial<BackgroundAlertState> = {}
): BackgroundAlertState => ({
  ...DEFAULT_BACKGROUND_ALERT_STATE,
  ...overrides,
});

const getBackgroundAlertState = async (): Promise<BackgroundAlertState> => {
  const parsed = safeParseJSON<BackgroundAlertState>(
    await AsyncStorage.getItem(BACKGROUND_ALERT_STATE_STORAGE_KEY)
  );

  if (!parsed || parsed.version !== 1) {
    return buildBackgroundAlertState();
  }

  return buildBackgroundAlertState(parsed);
};

const writeBackgroundAlertState = async (state: BackgroundAlertState) => {
  await AsyncStorage.setItem(BACKGROUND_ALERT_STATE_STORAGE_KEY, JSON.stringify(state));
};

const resetBackgroundAlertState = async () => {
  await writeBackgroundAlertState(buildBackgroundAlertState());
};

export const reconcileBackgroundAlertState = async ({
  isSessionActive,
  isDriving,
  notificationEnabled,
}: {
  isSessionActive: boolean;
  isDriving: boolean;
  notificationEnabled?: boolean;
}) => {
  if (isWeb) {
    return buildBackgroundAlertState();
  }

  const state = await getBackgroundAlertState();
  let nextState = state;
  let shouldDismissAlerts = false;
  let stateChanged = false;

  if (!isSessionActive || notificationEnabled === false) {
    // Session ended or notifications were disabled while the app was backgrounded.
    // Drop any remembered alert edge and let foreground own the cleanup.
    shouldDismissAlerts = state.activeAlert !== null;
    stateChanged = shouldDismissAlerts || state.lastDecision !== 'reset';
    if (stateChanged) {
      nextState = buildBackgroundAlertState({
        activeAlert: null,
        lastDecision: 'reset',
        lastScheduledAt: state.lastScheduledAt,
        updatedAt: Date.now(),
      });
    }
  } else if (isDriving && state.activeAlert === 'movement') {
    // Movement alerts only make sense before driving starts. Once the driver is back in driving
    // mode, foreground reconciliation clears that edge and keeps the persistent status
    // notification isolated from alert cleanup.
    shouldDismissAlerts = true;
    stateChanged = true;
    nextState = buildBackgroundAlertState({
      activeAlert: null,
      lastDecision: 'reset',
      lastScheduledAt: state.lastScheduledAt,
      updatedAt: Date.now(),
    });
  } else if (!isDriving && state.activeAlert === 'stationary') {
    // Stationary alerts only make sense while actively driving. If the app now says the user is
    // not driving, foreground reconciliation resets the stationary edge and dismisses only the
    // alert notifications.
    shouldDismissAlerts = true;
    stateChanged = true;
    nextState = buildBackgroundAlertState({
      activeAlert: null,
      lastDecision: 'reset',
      lastScheduledAt: state.lastScheduledAt,
      updatedAt: Date.now(),
    });
  } else if (isDriving && state.activeAlert === 'stationary') {
    // If the user starts moving again before TaskManager runs another location cycle, foreground
    // reconciliation still owns cleanup for the stale stationary alert.
    shouldDismissAlerts = true;
    stateChanged = true;
    nextState = buildBackgroundAlertState({
      activeAlert: null,
      lastDecision: 'reset',
      lastScheduledAt: state.lastScheduledAt,
      updatedAt: Date.now(),
    });
  }

  if (shouldDismissAlerts) {
    await dismissDrivingAlertNotifications();
  }

  if (stateChanged) {
    await writeBackgroundAlertState(nextState);
  }

  if (stateChanged || shouldDismissAlerts) {
    track('background_alert_reconciled', {
      isSessionActive,
      isDriving,
      notificationEnabled: notificationEnabled ?? null,
      active_alert_before: state.activeAlert,
      active_alert_after: nextState.activeAlert,
      dismissed_alerts: shouldDismissAlerts,
      state_changed: stateChanged,
    });
  }

  return nextState;
};

const clearTrackingStorage = async () => {
  await AsyncStorage.multiRemove([...TRACKING_STORAGE_KEYS]);
};

const getBackgroundLocationTaskStatus = async () => {
  if (isWeb) {
    return {
      isRegistered: false,
      hasStarted: false,
    };
  }

  const [isRegistered, hasStarted] = await Promise.all([
    TaskManager.isTaskRegisteredAsync(BACKGROUND_LOCATION_TASK).catch(() => false),
    Location.hasStartedLocationUpdatesAsync(BACKGROUND_LOCATION_TASK).catch(() => false),
  ]);

  return {
    isRegistered,
    hasStarted,
  };
};

const clearNativeBackgroundLocationTask = async () => {
  if (isWeb) {
    return false;
  }

  try {
    const { isRegistered, hasStarted } = await getBackgroundLocationTaskStatus();
    if (isRegistered || hasStarted) {
      await Location.stopLocationUpdatesAsync(BACKGROUND_LOCATION_TASK);
    }
    return isRegistered || hasStarted;
  } catch (error) {
    console.warn('Failed to clear background location task registration', error);
    return false;
  }
};

const repairAndroidBackgroundTrackingState = async () => {
  if (!isAndroid) {
    return false;
  }

  try {
    const { isRegistered, hasStarted } = await getBackgroundLocationTaskStatus();
    if (!isRegistered && !hasStarted) {
      return false;
    }

    if (isRegistered && hasStarted) {
      return false;
    }

    console.warn('Detected stale Android background location task state', {
      isRegistered,
      hasStarted,
    });

    await clearNativeBackgroundLocationTask();
    await clearTrackingStorage();
    return true;
  } catch (error) {
    console.warn('Failed to repair Android background location task state', error);
    return false;
  }
};

// Notification setup function (moved from module level)
const setupNotifications = async () => {
  if (Platform.OS === 'web') {
    return;
  }

  try {
    // Category for stationary alert (user is driving but stopped) - needs "Stop Driving" button
    await Notifications.setNotificationCategoryAsync(DRIVING_ALERT_NOTIFICATION_CATEGORY, [
      {
        identifier: STOP_DRIVING_ACTION_ID,
        buttonTitle: i18n.t('notifications.stopDriveButton', 'Stop Driving'),
        options: { opensAppToForeground: true },
      },
      {
        identifier: CANCEL_NOTIFICATION_ACTION_ID,
        buttonTitle: i18n.t('notifications.cancelButton', 'Cancel'),
        options: { isDestructive: false, opensAppToForeground: false },
      },
    ]);

    // Category for movement alert (user is moving but not driving) - needs "Start Driving" button
    await Notifications.setNotificationCategoryAsync(MOVEMENT_ALERT_NOTIFICATION_CATEGORY, [
      {
        identifier: START_DRIVING_ACTION_ID,
        buttonTitle: i18n.t('notifications.startDrivingButton', 'Start Driving'),
        options: { opensAppToForeground: true },
      },
      {
        identifier: CANCEL_NOTIFICATION_ACTION_ID,
        buttonTitle: i18n.t('notifications.cancelButton', 'Cancel'),
        options: { isDestructive: false, opensAppToForeground: false },
      },
    ]);
  } catch (error) {
    console.error('Error setting up notification categories:', error);
  }
};

interface UseLocationTrackingProps {
  handleStartDriving?: () => void;
  handleStopDriving: () => void;
  isSessionActive?: boolean;
  isDriving?: boolean;
  onMovementAlert?: () => void;
  onStationaryAlert?: () => void;
  onLocationSettingsError?: () => void;
}

export const useLocationTracking = ({
  handleStartDriving,
  handleStopDriving,
  isSessionActive = false,
  isDriving = false,
  onMovementAlert,
  onStationaryAlert,
  onLocationSettingsError,
}: UseLocationTrackingProps) => {
  const [lastLocation, setLastLocation] = useState<{latitude: number; longitude: number} | null>(null);
  const [stableLocation, setStableLocation] = useState<{latitude: number; longitude: number} | null>(null);
  const stableLocationTimestamp = useRef<number | null>(null);
  const foregroundLocationSubscription = useRef<Location.LocationSubscription | null>(null);
  const lastForegroundProcessTimestamp = useRef<number>(0);
  const lastMovementAlertTimestamp = useRef<number>(0);
  const movementAlertCallbackRef = useRef(onMovementAlert);
  const stationaryAlertCallbackRef = useRef(onStationaryAlert);
  const handleStartDrivingRef = useRef(handleStartDriving);
  const handleStopDrivingRef = useRef(handleStopDriving);
  const isStartingBackgroundTrackingRef = useRef(false);
  const pendingBackgroundStartRef = useRef(false);
  const backgroundRecoveryScheduledRef = useRef(false);
  const backgroundRecoveryAttemptedRef = useRef(false);
  const hasSeenDrivingStateRef = useRef(false);
  const isSessionActiveRef = useRef(isSessionActive);
  const isDrivingRef = useRef(isDriving);
  const [trackingDegraded, setTrackingDegraded] = useState(false);

  // Kept in the public props for source compatibility with older callers. Automatic
  // tracking failures are intentionally non-blocking and never invoke this callback.
  void onLocationSettingsError;

  useEffect(() => {
    movementAlertCallbackRef.current = onMovementAlert;
  }, [onMovementAlert]);

  useEffect(() => {
    stationaryAlertCallbackRef.current = onStationaryAlert;
  }, [onStationaryAlert]);


  useEffect(() => {
    handleStartDrivingRef.current = handleStartDriving;
  }, [handleStartDriving]);

  useEffect(() => {
    handleStopDrivingRef.current = handleStopDriving;
  }, [handleStopDriving]);

  useEffect(() => {
    isSessionActiveRef.current = isSessionActive;
    isDrivingRef.current = isDriving;
  }, [isSessionActive, isDriving]);

  const reportAutomaticTrackingDegradation = (
    stage: string,
    error: unknown,
    normalized: Error,
    taskStatus: { isRegistered: boolean; hasStarted: boolean } | null,
    initialLocation: boolean,
    captureException = true,
  ) => {
    setTrackingDegraded(true);
    track('driver_tracking_degraded', {
      stage,
      error_type: normalized.message,
      task_status: taskStatus,
      initial_location: initialLocation,
      register_task: stage === 'register_task',
    });

    if (captureException && !isUserStateLocationError(normalized)) {
      sentry.captureException(error, (scope) => {
        scope.setTag('location_error_type', normalized.message);
        scope.setExtra('stage', stage);
        scope.setExtra('task_status', taskStatus);
        scope.setExtra('initial_location', initialLocation);
        scope.setExtra('register_task', stage === 'register_task');
        scope.setExtra('platform', Platform.OS);
        scope.setExtra('isSessionActive', isSessionActiveRef.current);
        scope.setExtra('isDriving', isDrivingRef.current);
        return scope;
      });
    }
  };
  
  // Store session state in AsyncStorage when it changes
  useEffect(() => {
    const updateSessionState = async () => {
      await AsyncStorage.multiSet([
        ['isSessionActive', isSessionActive.toString()],
        ['isDriving', isDriving.toString()],
      ]);
    };
    updateSessionState();
  }, [isSessionActive, isDriving]);

  // Setup foreground location monitoring
  useEffect(() => {
    if (isWeb) return;
    
    const startForegroundMonitoring = async () => {
      try {
        const { status } = await Location.requestForegroundPermissionsAsync();

        if (status !== 'granted') {
          reportAutomaticTrackingDegradation(
            'foreground_permissions',
            new Error(LOCATION_ERROR.PERMISSION_DENIED),
            new Error(LOCATION_ERROR.PERMISSION_DENIED),
            null,
            false,
          );
          return;
        }

        // Get initial location
        const initialLocation = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.High,
        });

        if (!isValidLocationCoordinates(initialLocation?.coords)) {
          throw new Error(LOCATION_ERROR.INVALID_COORDINATES);
        }

        setTrackingDegraded(false);
        const coords = {
          latitude: initialLocation.coords.latitude,
          longitude: initialLocation.coords.longitude,
        };
        setLastLocation(coords);
        setStableLocation(coords);
        stableLocationTimestamp.current = Date.now();

        // Start watching position
        foregroundLocationSubscription.current = await Location.watchPositionAsync(
          {
            accuracy: Location.Accuracy.Balanced,
            timeInterval: FOREGROUND_UPDATE_INTERVAL,
            distanceInterval: FOREGROUND_DISTANCE_INTERVAL,
          },
          (location) => {
            if (!isValidLocationCoordinates(location?.coords)) {
              const invalidLocationError = new Error(LOCATION_ERROR.INVALID_COORDINATES);
              reportAutomaticTrackingDegradation(
                'foreground_update',
                invalidLocationError,
                invalidLocationError,
                null,
                false,
              );
              return;
            }

            const currentCoords = {
              latitude: location.coords.latitude,
              longitude: location.coords.longitude,
              accuracy: location.coords.accuracy ?? null,
            };
            handleForegroundLocationUpdate(currentCoords);
          }
        );
      } catch (error: unknown) {
        const normalized = normalizeLocationError(error);
        console.error('Error starting foreground location monitoring:', error);
        reportAutomaticTrackingDegradation('foreground_monitoring', error, normalized, null, false);
      }
    };

    if (isSessionActive) {
      startForegroundMonitoring().catch((err) => {
        console.error('Unhandled error in startForegroundMonitoring:', err);
      });
    }
    
    return () => {
      if (foregroundLocationSubscription.current) {
        foregroundLocationSubscription.current.remove();
      }
    };
  }, [isSessionActive, isDriving]);
  
  // Add a separate timer to check for stationary state, independent of location updates
  useEffect(() => {
    let stationaryCheckTimer: ReturnType<typeof setInterval> | null = null;
    
    const checkStationaryState = async () => {
      try {
        if (!isSessionActive || !isDriving || !stableLocation || !stableLocationTimestamp.current) return;
        
        // Get user settings
        const [notificationEnabled, stopTimeThreshold] = await Promise.all([
          AsyncStorage.getItem('notificationEnabled'),
          AsyncStorage.getItem('stopTimeThreshold')
        ]);
        
        if (notificationEnabled !== 'true') return;
        
        const userThreshold = parseInt(stopTimeThreshold || '5', 10);
        const now = Date.now();
        const stationaryTime = (now - stableLocationTimestamp.current) / 60000; // in minutes
        
        if (stationaryTime >= userThreshold) {
          // Call the stationary alert callback for foreground notification
          if (stationaryAlertCallbackRef.current) {
            stationaryAlertCallbackRef.current();
          }

          track('driver_stationary_alert_triggered', {
            source: 'foreground',
            threshold_minutes: userThreshold,
          });
          
          // Reset timestamp after alert
          stableLocationTimestamp.current = now;
        }
      } catch (error) {
        console.error('Error checking stationary state:', error);
      }
    };
    
    // Only start timer if in driving mode
    if (isSessionActive && isDriving && !isWeb) {
      stationaryCheckTimer = setInterval(checkStationaryState, STATIONARY_CHECK_INTERVAL);
      
      // Run an initial check
      checkStationaryState();
    }
    
    return () => {
      if (stationaryCheckTimer) {
        clearInterval(stationaryCheckTimer);
      }
    };
  }, [isSessionActive, isDriving, stableLocation]);
  
  // Handle foreground location updates
  const handleForegroundLocationUpdate = async (currentCoords: {latitude: number; longitude: number; accuracy?: number | null}) => {
    try {
      if (!isSessionActive) return;
      if (!isValidLocationCoordinates(currentCoords)) {
        const invalidLocationError = new Error(LOCATION_ERROR.INVALID_COORDINATES);
        reportAutomaticTrackingDegradation(
          'foreground_update',
          invalidLocationError,
          invalidLocationError,
          null,
          false,
        );
        return;
      }
      setTrackingDegraded(false);

      const now = Date.now();
      if (now - lastForegroundProcessTimestamp.current < FOREGROUND_PROCESSING_COOLDOWN_MS) {
        return;
      }
      lastForegroundProcessTimestamp.current = now;

      if (
        typeof currentCoords.accuracy === 'number' &&
        currentCoords.accuracy > MAX_ACCEPTED_ACCURACY_METERS
      ) {
        return;
      }
      
      // Get user settings
      const [notificationEnabled, distanceThresholdStr] = await Promise.all([
        AsyncStorage.getItem('notificationEnabled'),
        AsyncStorage.getItem('distanceThreshold')
      ]);
      
      if (notificationEnabled !== 'true') return;
      
      const userDistanceThreshold = parseInt(distanceThresholdStr || '100', 10);

      // Check for movement when session is active but not driving
      if (isSessionActive && !isDriving && lastLocation) {
        const distance = getDistance(lastLocation, currentCoords);
        
        if (distance > userDistanceThreshold) {
          const canTriggerMovementAlert =
            now - lastMovementAlertTimestamp.current >= MOVEMENT_ALERT_COOLDOWN_MS;

          if (canTriggerMovementAlert) {
            // Call the movement alert callback for foreground notification
            if (movementAlertCallbackRef.current) {
              movementAlertCallbackRef.current();
            }

            track('driver_movement_alert_triggered', {
              source: 'foreground',
              distance_meters: Math.round(distance),
            });

            lastMovementAlertTimestamp.current = now;
          }
        }
      }
      
      // When driving, update the stable location reference but let the timer handle alerts
      if (isSessionActive && isDriving) {
        const distance = stableLocation ? getDistance(stableLocation, currentCoords) : 0;
        
        if (distance > userDistanceThreshold) {
          // Update stable location if moved significantly
          setStableLocation(currentCoords);
          stableLocationTimestamp.current = now;
        }
      }
      
      // Update last location
      setLastLocation(currentCoords);
      
    } catch (error) {
      console.error('Error processing foreground location:', error);
    }
  };

  const startBackgroundTracking = async (isRecoveryAttempt = false) => {
    if (isWeb) {
      return false;
    }

    const currentIsSessionActive = isSessionActiveRef.current;
    const currentIsDriving = isDrivingRef.current;
    const appState = (AppState as unknown as { currentState?: string } | undefined)?.currentState;

    if (!currentIsSessionActive) {
      return false;
    }

    if (backgroundRecoveryScheduledRef.current && !isRecoveryAttempt) {
      return false;
    }

    // Android 12+ rejects foreground service starts when the app is in the background.
    // Queue one replay for the next active transition instead of retrying in a loop.
    if (Platform.OS === 'android' && appState !== 'active' && appState !== 'inactive') {
      pendingBackgroundStartRef.current = true;
      return false;
    }

    if (isStartingBackgroundTrackingRef.current) {
      return false;
    }

    isStartingBackgroundTrackingRef.current = true;
    let taskStatus: { isRegistered: boolean; hasStarted: boolean } = {
      isRegistered: false,
      hasStarted: false,
    };
    let stage = 'task_status';
    let initialLocationAvailable = false;

    try {
      taskStatus = await getBackgroundLocationTaskStatus();
      if (taskStatus.isRegistered && taskStatus.hasStarted) {
        pendingBackgroundStartRef.current = false;
        backgroundRecoveryScheduledRef.current = false;
        setTrackingDegraded(false);
        return true;
      }

      if (taskStatus.isRegistered || taskStatus.hasStarted) {
        await clearNativeBackgroundLocationTask();
      }
      // A new registration must not inherit a coordinate from an earlier task.
      await clearTrackingStorage();

      // Setup notifications before starting tracking (only on native)
      await setupNotifications();

      stage = 'permissions';
      const [backgroundStatus, notificationStatus] = await Promise.all([
        Location.requestBackgroundPermissionsAsync(),
        Notifications.requestPermissionsAsync(),
      ]);

      if (backgroundStatus.status !== 'granted' || notificationStatus.status !== 'granted') {
        const permissionError = new Error(LOCATION_ERROR.PERMISSION_DENIED);
        reportAutomaticTrackingDegradation('permissions', permissionError, permissionError, taskStatus, false);
        return false;
      }

      let currentLocation: Location.LocationObject | null = null;
      stage = 'initial_location';
      try {
        const candidateLocation = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.High,
        });
        if (!isValidLocationCoordinates(candidateLocation?.coords)) {
          throw new Error(LOCATION_ERROR.INVALID_COORDINATES);
        }
        currentLocation = candidateLocation;
        initialLocationAvailable = true;
        setTrackingDegraded(false);
      } catch (seedError: unknown) {
        const normalizedSeedError = normalizeLocationError(seedError);
        reportAutomaticTrackingDegradation(
          'initial_location',
          seedError,
          normalizedSeedError,
          taskStatus,
          false,
        );
      }

      stage = 'register_task';
      await Location.startLocationUpdatesAsync(BACKGROUND_LOCATION_TASK, {
        accuracy: Location.Accuracy.High,
        timeInterval: LOCATION_UPDATE_INTERVAL,
        showsBackgroundLocationIndicator: true,
        foregroundService: {
          notificationTitle: i18n.t('notifications.activeTracking', 'Active Tracking'),
          notificationBody: i18n.t('notifications.locationBeingMonitored', 'Your location is being monitored.'),
          killServiceOnDestroy: false,
        },
      });

      const now = Date.now();
      const storageEntries: Array<[string, string]> = [
        ['isSessionActive', currentIsSessionActive.toString()],
        ['isDriving', currentIsDriving.toString()],
      ];
      if (currentLocation) {
        const location = {
          latitude: currentLocation.coords.latitude,
          longitude: currentLocation.coords.longitude,
        };
        storageEntries.unshift(
          ['stableLocation', JSON.stringify(location)],
          ['locationTimestamp', now.toString()],
          ['lastLocation', JSON.stringify(location)],
        );
      }
      await AsyncStorage.multiSet(storageEntries);
      await writeBackgroundAlertState(
        buildBackgroundAlertState({
          updatedAt: now,
        })
      );

      pendingBackgroundStartRef.current = false;
      backgroundRecoveryScheduledRef.current = false;
      if (initialLocationAvailable) {
        setTrackingDegraded(false);
      }
      return true;
    } catch (error: unknown) {
      const normalized = normalizeLocationError(error);
      // FOREGROUND_SERVICE_NOT_ALLOWED is an Android-only condition. It is still
      // automatic degradation, so it gets one active-state recovery and no dialog.
      if (Platform.OS === 'android' && normalized.message === LOCATION_ERROR.FOREGROUND_SERVICE_NOT_ALLOWED) {
        pendingBackgroundStartRef.current = true;
        if (!isRecoveryAttempt && !backgroundRecoveryAttemptedRef.current) {
          backgroundRecoveryScheduledRef.current = true;
        }
        reportAutomaticTrackingDegradation('register_task', error, normalized, taskStatus, initialLocationAvailable, false);
        sentry.addBreadcrumb({
          category: 'location',
          level: 'warning',
          message: 'Foreground service start deferred: app in background',
          data: { appState, platform: Platform.OS },
        });
        return false;
      }

      console.error('Error starting tracking:', error);
      reportAutomaticTrackingDegradation(stage, error, normalized, taskStatus, initialLocationAvailable);
      if (
        stage === 'register_task' &&
        !isRecoveryAttempt &&
        !backgroundRecoveryAttemptedRef.current &&
        !backgroundRecoveryScheduledRef.current
      ) {
        pendingBackgroundStartRef.current = true;
        backgroundRecoveryScheduledRef.current = true;
      }
      return false;
    } finally {
      isStartingBackgroundTrackingRef.current = false;
    }
  };


  useEffect(() => {
    if (!isAndroid) {
      return;
    }

    void repairAndroidBackgroundTrackingState();
  }, []);

  const stopBackgroundTracking = async () => {
    if (isWeb) {
      return;
    }

    try {
      pendingBackgroundStartRef.current = false;
      backgroundRecoveryScheduledRef.current = false;
      backgroundRecoveryAttemptedRef.current = false;
      await clearNativeBackgroundLocationTask();
      if (!isWeb) {
        await dismissDrivingAlertNotifications();
      }
      await resetBackgroundAlertState();

      // Also stop foreground tracking
      if (foregroundLocationSubscription.current) {
        foregroundLocationSubscription.current.remove();
        foregroundLocationSubscription.current = null;
      }
    } catch (error) {
      console.error('Error stopping tracking:', error);
    }
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const subscription = Notifications.addNotificationResponseReceivedListener(response => {
      const actionId = response.actionIdentifier;

      // Handle stationary alert notifications (Stop Driving button)
      if (isDrivingAlertNotificationResponse(response)) {
        if (actionId === STOP_DRIVING_ACTION_ID) {
          track('driver_notification_action', {
            action: 'stop_drive',
            source: 'stationary_alert',
          });
          handleStopDrivingRef.current();
        } else if (actionId === CANCEL_NOTIFICATION_ACTION_ID) {
          track('driver_notification_action', {
            action: 'dismiss',
            source: 'stationary_alert',
          });
          // Dismiss the notification when user taps cancel
          void dismissDrivingAlertNotifications();
        }
        return;
      }

      // Handle movement alert notifications (Start Driving button)
      if (isMovementAlertNotificationResponse(response)) {
        if (actionId === START_DRIVING_ACTION_ID) {
          track('driver_notification_action', {
            action: 'start_drive',
            source: 'movement_alert',
          });
          handleStartDrivingRef.current?.();
        } else if (actionId === CANCEL_NOTIFICATION_ACTION_ID) {
          track('driver_notification_action', {
            action: 'dismiss',
            source: 'movement_alert',
          });
          // Dismiss the notification when user taps cancel
          void dismissDrivingAlertNotifications();
        }
        return;
      }
    });

    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    if (!isAndroid) return;
    if (typeof (AppState as any)?.addEventListener !== 'function') return;

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        void (async () => {
          await repairAndroidBackgroundTrackingState();
          if (pendingBackgroundStartRef.current && !backgroundRecoveryAttemptedRef.current) {
            pendingBackgroundStartRef.current = false;
            backgroundRecoveryAttemptedRef.current = true;
            backgroundRecoveryScheduledRef.current = false;
            await startBackgroundTracking(true);
          }
        })();
      }
    });

    return () => {
      subscription?.remove?.();
    };
  }, []);

  // Clear alert state only after an actual driving-mode transition. Skipping the initial mount
  // avoids unnecessary alert dismissal/storage churn before the state machine has seen any edge.
  useEffect(() => {
    if (!hasSeenDrivingStateRef.current) {
      hasSeenDrivingStateRef.current = true;
      return;
    }

    const clearAlertState = async () => {
      const errors: string[] = [];

      try {
        if (Platform.OS !== 'web') {
          try {
            await dismissDrivingAlertNotifications();
            track('driver_tracking_notification_dismiss', { success: true });
          } catch (e) {
            errors.push('notification_dismiss_failed');
            track('driver_tracking_notification_dismiss', { success: false, error: String(e) });
            // Non-critical, continue
          }
        }

        // Reset all tracking state in parallel for fresh start on driving mode transition
        try {
          await Promise.all([
            resetBackgroundAlertState(),
            AsyncStorage.multiRemove([
              'stableLocation',
              'locationTimestamp',
              'lastLocation',
            ]),
          ]);
          track('driver_tracking_storage_reset', { success: true, keys: ['stableLocation', 'locationTimestamp', 'lastLocation'] });
        } catch (e) {
          errors.push('state_reset_failed');
          track('driver_tracking_storage_reset', { success: false, error: String(e) });
          // Log but continue to reset ref
        }

        // Always reset the foreground tracking ref for consistency
        stableLocationTimestamp.current = null;

        track('driver_tracking_state_reset', {
          reason: 'driving_mode_change',
          is_driving: isDriving,
          errors: errors.length > 0 ? errors : undefined,
        });
      } catch (e) {
        // Final safety net
        console.error('Failed to clear alert state:', e);
        track('driver_tracking_state_reset_failed', { error: String(e) });
        stableLocationTimestamp.current = null; // Best effort
      }
    };

    void clearAlertState();
  }, [isDriving]);

  return { startBackgroundTracking, stopBackgroundTracking, trackingDegraded };
};

const backgroundLocationTaskHandler = async ({
  data,
  error,
}: {
  data?: BackgroundLocationTaskPayload;
  error?: unknown;
}) => {
  try {
    if (error) {
      console.error('Background task error:', error);
      return;
    }

    if (!data) {
      console.warn('Background task received null data');
      return;
    }

    const { locations } = data;
    if (!locations || locations.length === 0) return;

    const firstLocation = locations[0];
    if (!firstLocation?.coords) {
      console.warn('Background task received location without coords');
      return;
    }

    const currentCoords = {
      latitude: firstLocation.coords.latitude,
      longitude: firstLocation.coords.longitude,
    };

    if (!isValidLocationCoordinates(currentCoords)) {
      console.warn('Background task received invalid coordinates');
      return;
    }

    const backgroundAlertState = await getBackgroundAlertState();
    const [
      notificationEnabled,
      stopTimeThreshold,
      distanceThresholdStr,
      stableLocationStr,
      locationTimestampStr,
      lastLocationStr,
      isSessionActiveStr,
      isDrivingStr,
    ] = await Promise.all([
      AsyncStorage.getItem('notificationEnabled'),
      AsyncStorage.getItem('stopTimeThreshold'),
      AsyncStorage.getItem('distanceThreshold'),
      AsyncStorage.getItem('stableLocation'),
      AsyncStorage.getItem('locationTimestamp'),
      AsyncStorage.getItem('lastLocation'),
      AsyncStorage.getItem('isSessionActive'),
      AsyncStorage.getItem('isDriving'),
    ]);

    const now = Date.now();
    const isSessionActive = isSessionActiveStr === 'true';
    const isDriving = isDrivingStr === 'true';
    const notificationsAllowed = notificationEnabled === 'true';

    if (isSessionActive && isDriving) {
      await refreshDrivingStatusCountdown((key, options) => i18n.t(key, options), i18n.language, now);
    }

    if (!notificationsAllowed || !isSessionActive) {
      if (backgroundAlertState.activeAlert !== null) {
        await dismissDrivingAlertNotifications();
        await writeBackgroundAlertState(
          buildBackgroundAlertState({
            lastDecision: 'reset',
            lastScheduledAt: backgroundAlertState.lastScheduledAt,
            updatedAt: now,
          })
        );
      }
      return;
    }

    const userThreshold = parseInt(stopTimeThreshold || '5', 10);
    const userDistanceThreshold = parseInt(distanceThresholdStr || '100', 10);

    if (!isDriving) {
      const parsedLastLocation = safeParseJSON<{ latitude: number; longitude: number }>(lastLocationStr);
      const lastLocation = isValidLocationCoordinates(parsedLastLocation) ? parsedLastLocation : null;
      let nextState = buildBackgroundAlertState({
        ...backgroundAlertState,
        activeAlert:
          backgroundAlertState.activeAlert === 'movement' ? 'movement' : null,
        updatedAt: now,
      });

      if (lastLocation) {
        const distance = getDistance(lastLocation, currentCoords);

        if (distance > userDistanceThreshold) {
          // idle -> movement_edge: schedule once when the driver moves without being in driving mode.
          // movement_edge -> movement_duplicate: keep the alert latched and avoid re-scheduling.
          if (backgroundAlertState.activeAlert !== 'movement') {
            await Notifications.scheduleNotificationAsync({
              content: {
                title: i18n.t('notifications.attention', 'Attention'),
                body: i18n.t(
                  'notifications.movementDetectedBody',
                  'You are moving but not in driving mode. Please start driving mode.'
                ),
                categoryIdentifier: MOVEMENT_ALERT_NOTIFICATION_CATEGORY,
                sound: true,
              },
              trigger: null,
              identifier: getDrivingAlertNotificationId('movement'),
            });

            nextState = buildBackgroundAlertState({
              activeAlert: 'movement',
              lastDecision: 'movement_edge',
              lastScheduledAt: now,
              updatedAt: now,
            });
            track('background_alert_decision', {
              decision: nextState.lastDecision,
              alert_type: nextState.activeAlert,
              distance_meters: Math.round(distance),
              threshold_meters: userDistanceThreshold,
            });
          } else {
            nextState = buildBackgroundAlertState({
              ...backgroundAlertState,
              activeAlert: 'movement',
              lastDecision: 'movement_duplicate',
              updatedAt: now,
            });
          }
        } else if (backgroundAlertState.activeAlert === 'movement') {
          // movement_edge -> reset: the driver fell back under the threshold, so the next movement
          // crossing is allowed to schedule a fresh alert.
          nextState = buildBackgroundAlertState({
            activeAlert: null,
            lastDecision: 'reset',
            lastScheduledAt: backgroundAlertState.lastScheduledAt,
            updatedAt: now,
          });
        }
      }

      await Promise.all([
        AsyncStorage.setItem('lastLocation', JSON.stringify(currentCoords)),
        writeBackgroundAlertState(nextState),
      ]);
      return;
    }

    let nextState = buildBackgroundAlertState({
      ...backgroundAlertState,
      activeAlert:
        backgroundAlertState.activeAlert === 'stationary' ? 'stationary' : null,
      updatedAt: now,
    });

    if (!stableLocationStr || !locationTimestampStr) {
      await Promise.all([
        AsyncStorage.multiSet([
          ['stableLocation', JSON.stringify(currentCoords)],
          ['locationTimestamp', now.toString()],
        ]),
        writeBackgroundAlertState(nextState),
      ]);
      return;
    }

    const parsedStableLocation = safeParseJSON<{ latitude: number; longitude: number }>(stableLocationStr);
    const stableLocation = isValidLocationCoordinates(parsedStableLocation) ? parsedStableLocation : null;
    const locationTimestamp = Number.parseInt(locationTimestampStr || '', 10);

    if (!stableLocation || !Number.isFinite(locationTimestamp)) {
      console.warn('Invalid stable location data in background task', {
        stableLocationStr,
        locationTimestampStr,
      });
      await Promise.all([
        AsyncStorage.multiSet([
          ['stableLocation', JSON.stringify(currentCoords)],
          ['locationTimestamp', now.toString()],
        ]),
        writeBackgroundAlertState(nextState),
      ]);
      return;
    }

    const distance = getDistance(stableLocation, currentCoords);
    const stationaryTime = (now - locationTimestamp) / 60000;

    if (distance >= userDistanceThreshold) {
      // stationary_edge -> reset: movement beyond the threshold arms the state machine for the
      // next stationary dwell without doing any alert cleanup inside TaskManager.
      nextState = buildBackgroundAlertState({
        activeAlert: null,
        lastDecision: 'reset',
        lastScheduledAt: backgroundAlertState.lastScheduledAt,
        updatedAt: now,
      });
      await Promise.all([
        AsyncStorage.multiSet([
          ['stableLocation', JSON.stringify(currentCoords)],
          ['locationTimestamp', now.toString()],
        ]),
        writeBackgroundAlertState(nextState),
      ]);
      track('background_alert_decision', {
        decision: nextState.lastDecision,
        alert_type: 'stationary',
        distance_meters: Math.round(distance),
        threshold_meters: userDistanceThreshold,
      });
      return;
    }

    if (stationaryTime >= userThreshold) {
      const alertCooldownMs = userThreshold * 60 * 1000;
      const canRepeatStationaryAlert =
        backgroundAlertState.activeAlert === 'stationary' &&
        (backgroundAlertState.lastScheduledAt == null || now - backgroundAlertState.lastScheduledAt >= alertCooldownMs);

      // idle -> stationary_edge: crossing the dwell threshold schedules the first alert.
      // stationary_edge -> stationary_repeat: repeat on the same cadence while the driver remains stationary.
      // otherwise stay latched as stationary_duplicate until either cadence elapses or movement resets the state.
      if (backgroundAlertState.activeAlert !== 'stationary' || canRepeatStationaryAlert) {
        await Notifications.scheduleNotificationAsync({
          content: {
            title: i18n.t('notifications.attention', 'Attention'),
            body: i18n.t(
              'notifications.stationaryDetectedBody',
              'You have been stationary for more than {{minutes}} minutes. Consider stopping driving.',
              { minutes: userThreshold }
            ),
            categoryIdentifier: DRIVING_ALERT_NOTIFICATION_CATEGORY,
            sound: true,
          },
          trigger: null,
          identifier: getDrivingAlertNotificationId('stationary'),
        });

        nextState = buildBackgroundAlertState({
          activeAlert: 'stationary',
          lastDecision:
            backgroundAlertState.activeAlert === 'stationary' ? 'stationary_repeat' : 'stationary_edge',
          lastScheduledAt: now,
          updatedAt: now,
        });
      } else {
        nextState = buildBackgroundAlertState({
          ...backgroundAlertState,
          activeAlert: 'stationary',
          lastDecision: 'stationary_duplicate',
          updatedAt: now,
        });
      }

      await Promise.all([
        AsyncStorage.setItem('locationTimestamp', now.toString()),
        writeBackgroundAlertState(nextState),
      ]);
      if (nextState.lastDecision !== 'stationary_duplicate') {
        track('background_alert_decision', {
          decision: nextState.lastDecision,
          alert_type: nextState.activeAlert,
          stationary_minutes: Math.round(stationaryTime * 10) / 10,
          threshold_minutes: userThreshold,
        });
      }
      return;
    }

    await writeBackgroundAlertState(nextState);
  } catch (error) {
    console.error('Task processing error for background-location-task', error);
  }
};

// Exported for testing only — not part of the public API.
export const _backgroundLocationTaskHandlerForTesting = backgroundLocationTaskHandler;

// ALL-123: expo-task-manager (Android) drops a headless-bound task manager 2 s after the first
// task event it tracks for the app finishes (it only tracks the event that arrived while none was
// pending). When Android started the process headless (app update, reboot, OS kill) and the driver
// then opens the app, the activity reuses that runtime, so the drop leaves the task unbound and no
// later location tick reaches JS. Keeping the first task event that reaches this JS runtime pending
// stops that drop; the job itself still ends on the native timeout and later events run normally.
const NEVER_SETTLES = new Promise<void>(() => undefined);
let hasPinnedTaskEvent = false;

export const _resetPinnedTaskEventForTesting = () => {
  hasPinnedTaskEvent = false;
};

const backgroundLocationTaskExecutor = async (body: Parameters<typeof backgroundLocationTaskHandler>[0]) => {
  const pinThisEvent = isAndroid && !hasPinnedTaskEvent;
  if (pinThisEvent) hasPinnedTaskEvent = true;
  try {
    await backgroundLocationTaskHandler(body);
  } finally {
    if (pinThisEvent) await NEVER_SETTLES;
  }
};

export const _backgroundLocationTaskExecutorForTesting = backgroundLocationTaskExecutor;

const bootstrapBackgroundLocationTask = () => {
  if (!shouldEnableNativeBackgroundLocationTask) {
    return;
  }

  try {
    const isTaskDefined = TaskManager.isTaskDefined;
    const defineTask = TaskManager.defineTask;

    if (typeof isTaskDefined !== 'function' || typeof defineTask !== 'function') {
      console.warn('TaskManager background bootstrap unavailable; skipping Android task definition');
      return;
    }

    try {
      if (!isTaskDefined(BACKGROUND_LOCATION_TASK)) {
        defineTask(BACKGROUND_LOCATION_TASK, backgroundLocationTaskExecutor);
      }
    } catch (error) {
      console.warn('Failed to bootstrap Android background location task', error);
    }
  } catch (error) {
    console.warn('Failed to inspect Android background location task bootstrap', error);
  }
};

bootstrapBackgroundLocationTask();
